import React, { useEffect, useState } from "react";
import Template from "./Template";
import { useActions } from "../redux/actions";
import * as paymentTypesActions from "../redux/actions/paymentTypes";
import * as practiceActions from "../redux/actions/practice";
import { Button, Checkbox, Dropdown, Form, Input, Label, Message, Table } from "semantic-ui-react";

function OdPaymentTypes() {
    const PaymentTypesActions = useActions(paymentTypesActions);
    const PracticeActions = useActions(practiceActions);

    const [practiceOpts, setPracticeOpts] = useState<any[]>([]);
    const [selectedPracticeIds, setSelectedPracticeIds] = useState<number[]>([]);
    const [includeHidden, setIncludeHidden] = useState(false);
    const [definitions, setDefinitions] = useState<any[]>([]);
    const [searchDone, setSearchDone] = useState(false);
    const [filterPractice, setFilterPractice] = useState<string>("all");
    const [filterItemName, setFilterItemName] = useState<string>("");

    const createInit = {
        ItemName: "",
        isHidden: false,
    };
    const [createForm, setCreateForm] = useState(createInit);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [operationResults, setOperationResults] = useState<{ practice: string; success: boolean; error?: string }[] | null>(null);

    const ALL_PRACTICES = -1;

    useEffect(() => {
        PracticeActions.getAllPractices().then((res: any) => {
            const opts = [
                { key: ALL_PRACTICES, text: "All Practices", value: ALL_PRACTICES },
                ...res.map((item: any) => ({
                    key: item.practiceId,
                    text: item.practice.practice,
                    value: item.practiceId,
                })),
            ];
            setPracticeOpts(opts);
        });
    }, []);

    const handlePracticeChange = (value: number[]) => {
        const justAddedAll = value.includes(ALL_PRACTICES) && !selectedPracticeIds.includes(ALL_PRACTICES);
        if (justAddedAll) {
            setSelectedPracticeIds([ALL_PRACTICES]);
        } else {
            setSelectedPracticeIds(value.filter((v) => v !== ALL_PRACTICES));
        }
    };

    const resolvePracticeIds = () => {
        const realIds = practiceOpts.filter((o: any) => o.value !== ALL_PRACTICES).map((o: any) => o.value);
        if (!selectedPracticeIds.length || selectedPracticeIds.includes(ALL_PRACTICES)) return realIds;
        return selectedPracticeIds;
    };

    const flattenResults = (res: any[]) => {
        const rows: any[] = [];
        res.forEach((entry: any) => {
            if (entry.success && Array.isArray(entry.data)) {
                entry.data.forEach((def: any) => {
                    rows.push({ ...def, _practice: entry.practice });
                });
            }
        });
        return rows;
    };

    const handleLoad = () => {
        const ids = resolvePracticeIds();
        const params: any = { includeHidden };

        PaymentTypesActions.getOdPaymentTypes(ids, params).then((res: any) => {
            setDefinitions(Array.isArray(res) ? flattenResults(res) : []);
            setFilterPractice("all");
            setFilterItemName("");
            setSearchDone(true);
        });
    };

    const getVisibleRows = () => definitions.filter((d: any) =>
        (filterPractice === "all" || d._practice === filterPractice) &&
        (!filterItemName.trim() || (d.ItemName || "").toLowerCase().includes(filterItemName.toLowerCase()))
    );

    const handleCreate = () => {
        if (!createForm.ItemName) {
            alert("ItemName is required.");
            return;
        }
        const ids = resolvePracticeIds();
        const payload: any = {
            practiceIds: ids,
            ItemName: createForm.ItemName,
            isHidden: createForm.isHidden,
        };

        PaymentTypesActions.createOdPaymentType(payload).then((res: any) => {
            if (Array.isArray(res)) {
                setOperationResults(res.map((r: any) => ({
                    practice: r.practice,
                    success: !!r.success,
                    error: r.error
                })));
                setCreateForm(createInit);
                setShowCreateForm(false);
                handleLoad();
            }
        });
    };

    return (
        <Template activeLink="od-payment-types">
            <div className="ui card">
                <div className="content">
                    <h2>OD Payment Types</h2>
                    <Form>
                        <Form.Group widths="equal">
                            <Form.Field>
                                <label>Practices</label>
                                <Dropdown
                                    placeholder="All Practices"
                                    options={practiceOpts}
                                    selection={true}
                                    search={true}
                                    multiple={true}
                                    value={selectedPracticeIds}
                                    onChange={(_e, { value }) => handlePracticeChange(value as number[])}
                                />
                            </Form.Field>
                            <Form.Field style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
                                <Checkbox
                                    label="Include Hidden"
                                    checked={includeHidden}
                                    onChange={(_e, { checked }) => setIncludeHidden(!!checked)}
                                />
                            </Form.Field>
                            <Form.Field style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                                <Button primary onClick={handleLoad}>Search</Button>
                                <Button onClick={() => setShowCreateForm(!showCreateForm)}>
                                    {showCreateForm ? "Cancel" : "Create Payment Type"}
                                </Button>
                            </Form.Field>
                        </Form.Group>
                    </Form>
                </div>
            </div>

            {showCreateForm && (
                <div className="ui card">
                    <div className="content">
                        <h3>Create Payment Type</h3>
                        <p className="text-muted">Will be created in {selectedPracticeIds.length && !selectedPracticeIds.includes(ALL_PRACTICES) ? `${selectedPracticeIds.length} selected practice(s)` : 'all practices'}.</p>
                        <Form>
                            <Form.Group widths="equal">
                                <Form.Field required>
                                    <label>ItemName</label>
                                    <Input
                                        value={createForm.ItemName}
                                        onChange={(e) => setCreateForm({ ...createForm, ItemName: e.target.value })}
                                    />
                                </Form.Field>
                                <Form.Field style={{ display: "flex", alignItems: "flex-end" }}>
                                    <Checkbox
                                        label="isHidden"
                                        checked={createForm.isHidden}
                                        onChange={(_e, { checked }) => setCreateForm({ ...createForm, isHidden: !!checked })}
                                    />
                                </Form.Field>
                            </Form.Group>
                            <Button primary onClick={handleCreate}>Submit</Button>
                        </Form>
                    </div>
                </div>
            )}

            {operationResults && (
                <Message
                    onDismiss={() => setOperationResults(null)}
                    positive={operationResults.every(r => r.success)}
                    negative={operationResults.some(r => !r.success)}
                >
                    <Message.Header>
                        {operationResults.filter(r => r.success).length} succeeded, {operationResults.filter(r => !r.success).length} failed
                    </Message.Header>
                    <Message.List>
                        {operationResults.map((r, i) => (
                            <Message.Item key={i}>
                                <strong>{r.practice}</strong>: {r.success ? 'Success' : r.error || 'Failed'}
                            </Message.Item>
                        ))}
                    </Message.List>
                </Message>
            )}

            {searchDone && (
                <div className="ui card">
                    <div className="content">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                            <h3 style={{ margin: 0 }}>Results ({getVisibleRows().length})</h3>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <Input
                                    placeholder="Filter by ItemName..."
                                    value={filterItemName}
                                    onChange={(e) => setFilterItemName(e.target.value)}
                                    size="small"
                                />
                                <Dropdown
                                    placeholder="Filter by Practice"
                                    selection={true}
                                    clearable={true}
                                    options={Array.from(new Set(definitions.map((d: any) => d._practice))).map((p: any) => ({ key: p, text: p, value: p }))}
                                    value={filterPractice === "all" ? "" : filterPractice}
                                    onChange={(_e, { value }) => setFilterPractice(value ? value as string : "all")}
                                />
                            </div>
                        </div>
                        <Table celled={true}>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>Practice</Table.HeaderCell>
                                    <Table.HeaderCell>DefNum</Table.HeaderCell>
                                    <Table.HeaderCell style={{ width: '50%' }}>ItemName</Table.HeaderCell>
                                    <Table.HeaderCell>isHidden</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {getVisibleRows().map((def: any, idx: number) => (
                                    <Table.Row key={`${def._practice}-${def.DefNum}-${idx}`}>
                                        <Table.Cell><Label>{def._practice}</Label></Table.Cell>
                                        <Table.Cell>{def.DefNum}</Table.Cell>
                                        <Table.Cell>{def.ItemName}</Table.Cell>
                                        <Table.Cell>{String(def.isHidden) === "true" || def.isHidden === 1 ? "Yes" : "No"}</Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </div>
                </div>
            )}
        </Template>
    );
}

export default OdPaymentTypes;
